import type { AppCheck } from "firebase/app-check";
import { APP_CHECK_FAILURE, reportAppCheckFailure } from "../errorReporting";
import { classifyAppCheckError, recordAppCheckAttempt, recordRecaptchaUnavailable } from "./appCheckRecovery";

/**
 * THE ONE WAY AN API REQUEST GETS ITS `X-Firebase-AppCheck` TOKEN, on App and
 * Book. Each app's lib/firebase.ts starts App Check its own way (Book at once,
 * App after first paint, `whenAppCheckStarted`) and hands the result here,
 * with the SDK's `getToken`. Type-only Firebase import: the SDK itself is
 * passed in, so this module adds nothing to a chunk.
 *
 * Every attempt is RECORDED (appCheckRecovery.ts): null on a token, otherwise
 * why there was none. That record is what lets a screen's Try again reload the
 * page when App Check is why its read failed (`retryApiRead`): after a 403 the
 * SDK refuses every exchange for a day, in page memory only, so only a reload
 * can try again.
 *
 * And every failure that is App Check's own is REPORTED once per page, as a
 * fixed kind and nothing else (shared/errorReporting): the SDK's error, and
 * any token-shaped text in it, is never handed over. A plain network failure
 * is not an App Check failure and is not reported.
 *
 * THE WAIT IS BOUNDED. The token comes from reCAPTCHA, fetched from a Google
 * origin, and that fetch does not fail fast when it is blocked. An ad
 * blocker, a corporate network, a captive portal or a region that cannot
 * reach Google all leave the SDK's promise pending forever; the GraphQL
 * header link awaits it, so the query is never sent and the page sits on its
 * loading state with no error and no way out. Past the bound the request goes
 * without the header, the backend refuses it (App Check is hard-required),
 * and the page shows a failure its Try again can recover from. Found on Book
 * by the e2e stub aborting reCAPTCHA, which is exactly what an ad blocker
 * does; App shares the rule since c3.
 */
export const APP_CHECK_TIMEOUT_MS = 10_000;

/**
 * reCAPTCHA's script loaded but App Check cannot use it (it threw "no
 * recaptcha" from its load handler; contained by appCheckStart.ts). The
 * classified path, never the SDK's text: recorded for Try again, reported once
 * per page as the fixed `app_check_failure:unavailable`.
 */
export function recaptchaUnavailable(): void {
  recordRecaptchaUnavailable();
  reportAppCheckFailure(APP_CHECK_FAILURE.unavailable);
}

export type FetchAppCheckToken = (appCheck: AppCheck, forceRefresh: boolean) => Promise<{ token: string }>;

export async function appCheckHeaderToken(
  appCheck: AppCheck | null,
  fetchToken: FetchAppCheckToken,
  timeoutMs: number = APP_CHECK_TIMEOUT_MS
): Promise<string | null> {
  if (!appCheck) {
    // App Check could not start in this browser.
    recordAppCheckAttempt("unreachable");
    reportAppCheckFailure(APP_CHECK_FAILURE.unavailable);
    return null;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const token = await Promise.race([
      fetchToken(appCheck, /* forceRefresh */ false).then((result) => result.token),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
    // No token within the bound: reCAPTCHA never answered.
    recordAppCheckAttempt(token === null ? "unreachable" : null);
    if (token === null) {
      reportAppCheckFailure(APP_CHECK_FAILURE.timeout);
      console.error(
        `App Check did not produce a token within ${timeoutMs}ms — reCAPTCHA is ` +
          "probably blocked. Continuing without the header; the request will be rejected."
      );
    }
    return token;
  } catch (error) {
    // Refused, throttled or unreachable (appCheckRecovery.ts): Try again must
    // reload, and Book's words say which.
    const failure = classifyAppCheckError(error);
    recordAppCheckAttempt(failure);
    if (failure === "refused") reportAppCheckFailure(APP_CHECK_FAILURE.refused);
    else if (failure === "unreachable") reportAppCheckFailure(APP_CHECK_FAILURE.unavailable);
    console.error("Failed to obtain an App Check token.", error);
    return null;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
